import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * How long the user's own scrolling suspends auto-follow. Long enough to read a passage
 * without being yanked back, short enough that they don't have to wonder whether following
 * is broken.
 */
const USER_SCROLL_SUSPEND_MS = 5000;

/**
 * Keeps the active row of a follow-along list — a transcript line, a scene — centred as
 * playback moves through it, and stops fighting the user the moment they scroll the list
 * themselves.
 *
 * Wire both returns up or neither works:
 * - `activeRef` goes on the row that is currently active (and only that one).
 * - `scrollRef` goes on the element that actually **scrolls**. That is not always the
 *   element you'd guess: inside a `GradientScrollDiv` the scroller is the inner div, which
 *   is what the component's forwarded ref points at. A `scroll` event does not bubble, so a
 *   handler on an ancestor never fires and auto-follow would silently never yield.
 */
export function useAutoScrollToActive<A extends HTMLElement>({
  // `center` reads best for short rows — a transcript line sits mid-panel with context either
  // side. Rows that can grow taller than the panel want `nearest`, which aligns their top:
  // centring one of those puts its heading off-screen and shows the middle of a paragraph.
  block = 'center',
}: { block?: ScrollLogicalPosition } = {}) {
  const activeRef = useRef<A | null>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const suspendTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = useCallback(() => {
    setIsUserScrolling(true);
    if (suspendTimer.current) {
      clearTimeout(suspendTimer.current);
    }
    suspendTimer.current = setTimeout(() => setIsUserScrolling(false), USER_SCROLL_SUSPEND_MS);
  }, []);

  useEffect(
    () => () => {
      if (suspendTimer.current) {
        clearTimeout(suspendTimer.current);
        suspendTimer.current = null;
      }
    },
    [],
  );

  const scrolledElement = useRef<HTMLElement | null>(null);
  const scrollRef = useCallback(
    (node: HTMLElement | null) => {
      scrolledElement.current?.removeEventListener('scroll', handleScroll);
      scrolledElement.current = node;
      node?.addEventListener('scroll', handleScroll);
    },
    [handleScroll],
  );

  useEffect(() => {
    if (isUserScrolling) return;
    // `activeRef.current` is deliberately in the dep list: the active row moving from one
    // element to another is the only signal that there is somewhere new to scroll to, and
    // there is no state change to ride on. Deps are read at render time, one commit behind
    // the ref, so the scroll lands a render late — invisible next to the smooth behaviour,
    // and playback re-renders continuously anyway.
    activeRef.current?.scrollIntoView({ behavior: 'smooth', block, inline: 'nearest' });
  }, [activeRef.current, isUserScrolling, block]);

  return { activeRef, scrollRef };
}
